import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Field } from '@/components/synapse/Field';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { translateApiError } from '@/lib/apiError';
import {
  useCreateLaboratory,
  useLabLaboratories,
  useUpdateLaboratory,
} from './useLab';
import type { LabLaboratory } from '@/types/lab';

/**
 * The labs this clinic sends work to.
 *
 * Lives on the test-list screen rather than a route of its own: both are the lab
 * desk's reference data, both sit behind `lab.catalogue.manage`, and a clinic
 * holds a handful of laboratories — a destination in the nav for a one-field
 * form would cost more attention than it returns.
 *
 * A laboratory is never deleted, only hidden. Orders already routed to one keep
 * naming it, which is what lets `lab_orders` carry an FK instead of a snapshot
 * of the name.
 */
export function LaboratoriesSection() {
  const { t } = useTranslation();

  // The management view must see what was switched off — a list that hides its
  // own hidden rows cannot un-hide them.
  const laboratories = useLabLaboratories({ includeInactive: true });
  const create = useCreateLaboratory();
  const update = useUpdateLaboratory();

  const [error, setError] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');

  const rows = useMemo(() => laboratories.data ?? [], [laboratories.data]);
  const existingNames = useMemo(
    () => new Set(rows.map((row) => row.name.toLowerCase())),
    [rows],
  );

  const trimmed = draftName.trim();
  const duplicate = trimmed !== '' && existingNames.has(trimmed.toLowerCase());
  const busy = create.isPending || update.isPending;

  async function add() {
    setError(null);
    if (!trimmed) {
      setError(t('lab.laboratories.errors.incomplete'));
      return;
    }
    // Checked here so an obvious clash costs no round trip; the server's unique
    // index is still the authority.
    if (duplicate) {
      setError(t('lab.laboratories.errors.duplicate'));
      return;
    }
    try {
      await create.mutateAsync({ name: trimmed });
      setDraftName('');
    } catch (err) {
      setError(translateApiError(err, { fallback: t('lab.laboratories.errors.saveFailed') }));
    }
  }

  async function patch(id: string, input: { name?: string; active?: boolean }) {
    setError(null);
    try {
      await update.mutateAsync({ id, input });
    } catch (err) {
      setError(translateApiError(err, { fallback: t('lab.laboratories.errors.saveFailed') }));
    }
  }

  return (
    <section className="flex flex-col gap-3">
      {/* No heading of its own — the tab that selected this section names it. */}
      <p className="text-[13px] text-muted">{t('lab.laboratories.subtitle')}</p>

      {error && (
        <p role="alert" className="text-[13px] text-destructive">
          {error}
        </p>
      )}

      <div className="flex flex-col gap-2 rounded-md border border-line-2 bg-paper px-3.5 py-3">
        <h3 className="text-[13px] font-semibold text-ink">{t('lab.laboratories.addTitle')}</h3>
        <div className="flex flex-wrap items-start gap-2">
          <Field
            label={t('lab.laboratories.name')}
            className="min-w-[220px] flex-1"
            error={duplicate ? t('lab.laboratories.errors.duplicate') : undefined}
          >
            {(id) => (
              <Input
                id={id}
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
              />
            )}
          </Field>
          {/* Blank stand-in for a Field label, so the button lines up with the input. */}
          <div className="flex flex-col gap-1.5">
            <span aria-hidden className="text-xs">
              &nbsp;
            </span>
            {/* Named explicitly: the test list above this section has its own
                "Add", and two identically-labelled buttons on one screen are
                ambiguous to a screen reader. */}
            <Button
              onClick={() => void add()}
              disabled={busy}
              aria-label={t('lab.laboratories.addTitle')}
              className="h-10"
            >
              {t('common.add')}
            </Button>
          </div>
        </div>
      </div>

      {laboratories.isError && (
        <p className="text-[13px] text-destructive">{t('lab.laboratories.loadFailed')}</p>
      )}

      {!laboratories.isPending && rows.length === 0 && (
        <p className="text-[13px] text-muted">{t('lab.laboratories.empty')}</p>
      )}

      {rows.length > 0 && (
        <>
          <ul className="flex flex-col gap-1.5">
            {rows.map((laboratory) => (
              <li key={laboratory.id}>
                <LaboratoryRow
                  laboratory={laboratory}
                  busy={busy}
                  onRename={(name) => void patch(laboratory.id, { name })}
                  onToggle={(active) => void patch(laboratory.id, { active })}
                />
              </li>
            ))}
          </ul>
          <p className="text-[12.5px] text-muted">{t('lab.laboratories.inUseHint')}</p>
        </>
      )}
    </section>
  );
}

function LaboratoryRow({
  laboratory,
  busy,
  onRename,
  onToggle,
}: {
  laboratory: LabLaboratory;
  busy: boolean;
  onRename: (name: string) => void;
  onToggle: (active: boolean) => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(laboratory.name);

  function commit() {
    const trimmed = name.trim();
    setEditing(false);
    // A no-op rename is not a save: it would write an audit row saying nothing
    // changed.
    if (!trimmed || trimmed === laboratory.name) {
      setName(laboratory.name);
      return;
    }
    onRename(trimmed);
  }

  return (
    <div
      className={
        'flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-line-2 bg-paper px-3 py-2.5 ' +
        (laboratory.active ? '' : 'opacity-60')
      }
    >
      {editing ? (
        <Input
          value={name}
          autoFocus
          // Names the ROW, not the field: the add form above has a "Laboratory
          // name" box, and several rows may be renamed in one sitting.
          aria-label={t('lab.laboratories.renameLab', { lab: laboratory.name })}
          onChange={(e) => setName(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') {
              setName(laboratory.name);
              setEditing(false);
            }
          }}
          inputSize="sm"
          className="min-w-[200px] flex-1"
        />
      ) : (
        <span className="min-w-[160px] flex-1 text-[13.5px] text-ink">{laboratory.name}</span>
      )}

      {!laboratory.active && (
        <span className="rounded-full border border-line-2 px-2 py-0.5 text-[11px] text-muted">
          {t('lab.laboratories.hidden')}
        </span>
      )}

      {!editing && (
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          aria-label={t('lab.laboratories.renameLab', { lab: laboratory.name })}
          onClick={() => setEditing(true)}
        >
          {t('lab.laboratories.rename')}
        </Button>
      )}

      <Button
        size="sm"
        variant="secondary"
        disabled={busy}
        onClick={() => onToggle(!laboratory.active)}
        aria-label={t(laboratory.active ? 'lab.laboratories.hideLab' : 'lab.laboratories.showLab', {
          lab: laboratory.name,
        })}
      >
        {t(laboratory.active ? 'lab.laboratories.hide' : 'lab.laboratories.show')}
      </Button>
    </div>
  );
}
